import {
  Box,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { MdaClaim, MdaValueFilterField } from "./mda-types";

export const formatDate = (value: string | null) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getRiskChipColor = (riskRating: string) => {
  switch (riskRating.toLowerCase()) {
    case "high":
      return "#7f1d1d";
    case "medium":
      return "#78350f";
    case "low":
      return "#14532d";
    default:
      return "#1e3a8a";
  }
};

export const getRiskChipBackground = (riskRating: string) => {
  switch (riskRating.toLowerCase()) {
    case "high":
      return "#fee2e2";
    case "medium":
      return "#fef3c7";
    case "low":
      return "#dcfce7";
    default:
      return "#dbeafe";
  }
};

export const getPreviousScoreChipColors = (previousScore: string) => {
  // Never-scored claims come back as "N/A"; keep them visually neutral rather
  // than falling through to the blue "unknown rating" chip.
  if (!["high", "medium", "low"].includes(previousScore.toLowerCase())) {
    return { backgroundColor: "#f1f5f9", color: "#475569" };
  }

  return {
    backgroundColor: getRiskChipBackground(previousScore),
    color: getRiskChipColor(previousScore),
  };
};

export const getStatusChipColors = (status: string) => {
  switch (status.toLowerCase()) {
    case "open":
      return { backgroundColor: "#dbeafe", color: "#2563eb" };
    case "under review":
    case "filehandling":
      return { backgroundColor: "#e0e7ff", color: "#4f46e5" };
    case "approved":
    case "resolved":
    case "paid":
      return { backgroundColor: "#dcfce7", color: "#16a34a" };
    case "on hold":
    case "pending":
      return { backgroundColor: "#fef3c7", color: "#d97706" };
    case "closed":
      return { backgroundColor: "#ede9fe", color: "#7c3aed" };
    case "rejected":
    case "cancelled":
      return { backgroundColor: "#fee2e2", color: "#dc2626" };
    default:
      return { backgroundColor: "#f1f5f9", color: "#475569" };
  }
};

export const renderBadge = (
  label: string,
  backgroundColor: string,
  color: string,
) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    }}
  >
    <Chip
      label={label || "-"}
      size="small"
      sx={{
        height: 22,
        borderRadius: 999,
        backgroundColor,
        color,
        fontSize: "0.75rem",
        fontWeight: 700,
        "& .MuiChip-label": {
          px: 1.25,
        },
      }}
    />
  </Box>
);

export const getClaimRowId = (claim: MdaClaim) =>
  `${claim.entityId}-${claim.fileId}-${claim.claimReference}`;

export const isClaimIncluded = (claim: MdaClaim) => !claim.selected;

export const VALUE_FILTER_FIELDS: MdaValueFilterField[] = [
  "clientName",
  "insurer",
  "status",
  "riskRating",
  "previousScore",
  "claimType",
];

export interface MdaColumnOptions {
  /** Row ids left after all table filters; when omitted every claim counts as visible. */
  visibleRowIds?: Set<string>;
  renderValueFilter?: (
    field: MdaValueFilterField,
    headerName: string,
  ) => React.ReactNode;
}

const getRuleBreaksColumn = (
  onViewRuleBreaks: (claim: MdaClaim) => void,
): GridColDef<MdaClaim> => ({
  field: "ruleBreaks",
  type: "actions",
  headerName: "Rule breaks",
  width: 100,
  getActions: ({ row }) => [
    <Tooltip key="rule-breaks" title="View rule breaks">
      <IconButton
        aria-label={`View rule breaks for ${row.claimReference}`}
        size="small"
        onClick={(event) => {
          // Keep the click from also selecting the row.
          event.stopPropagation();
          onViewRuleBreaks(row);
        }}
        sx={{ color: "#1769ff" }}
      >
        <RuleOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>,
  ],
});

const getClaimColumns = (
  claims: MdaClaim[],
  onClaimsChange: (claims: MdaClaim[]) => void,
  visibleRowIds?: Set<string>,
): GridColDef<MdaClaim>[] => [
  {
    field: "selected",
    headerName: "",
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => {
      const isVisible = (claim: MdaClaim) =>
        !visibleRowIds || visibleRowIds.has(getClaimRowId(claim));
      const visibleClaims = claims.filter(isVisible);
      const selectedCount = visibleClaims.filter(isClaimIncluded).length;
      const hasClaims = visibleClaims.length > 0;
      const hasEveryClaimSelected =
        hasClaims && selectedCount === visibleClaims.length;

      return (
        <Checkbox
          checked={hasEveryClaimSelected}
          disabled={!hasClaims}
          indeterminate={selectedCount > 0 && !hasEveryClaimSelected}
          onChange={(event) => {
            const { checked } = event.target;
            // Claims hidden by a table filter always stay unticked.
            onClaimsChange(
              claims.map((claim) => ({
                ...claim,
                selected: isVisible(claim) ? !checked : true,
              })),
            );
          }}
          size="small"
        />
      );
    },
    renderCell: ({ row }) => (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Checkbox
          checked={isClaimIncluded(row)}
          onChange={(event) => {
            const { checked } = event.target;
            const rowId = getClaimRowId(row);
            onClaimsChange(
              claims.map((claim) =>
                getClaimRowId(claim) === rowId
                  ? { ...claim, selected: !checked }
                  : claim,
              ),
            );
          }}
          size="small"
        />
      </Box>
    ),
  },
  {
    field: "claimReference",
    headerName: "Claim reference",
    flex: 1,
    minWidth: 170,
    renderCell: ({ value }) => (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ color: "#1769ff", fontSize: "0.875rem", fontWeight: 600 }}
        >
          {value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "clientName",
    headerName: "Client",
    flex: 1,
    minWidth: 150,
  },
  {
    field: "insurer",
    headerName: "Insurer",
    flex: 1,
    minWidth: 150,
    valueFormatter: (value: string | null) => value || "-",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.8,
    minWidth: 130,
    renderCell: ({ row }) => {
      const { backgroundColor, color } = getStatusChipColors(row.status);

      return renderBadge(row.status, backgroundColor, color);
    },
  },
  {
    field: "riskRating",
    headerName: "Risk rating",
    flex: 0.8,
    minWidth: 130,
    renderCell: ({ row }) => {
      const color = getRiskChipColor(row.riskRating);
      const backgroundColor = getRiskChipBackground(row.riskRating);

      return renderBadge(row.riskRating, backgroundColor, color);
    },
  },
  {
    field: "previousScore",
    headerName: "Previous score",
    flex: 0.8,
    minWidth: 130,
    renderCell: ({ row }) => {
      const { backgroundColor, color } = getPreviousScoreChipColors(
        row.previousScore,
      );

      return renderBadge(row.previousScore, backgroundColor, color);
    },
  },
  {
    field: "previousScoreDate",
    headerName: "Previous score date",
    flex: 0.9,
    minWidth: 150,
    valueFormatter: (value: string | null) => formatDate(value),
  },
  {
    field: "incidentDate",
    headerName: "Incident date",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: (value: string) => formatDate(value),
  },
  {
    field: "importDate",
    headerName: "Import date",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: (value: string) => formatDate(value),
  },
  {
    field: "claimType",
    headerName: "Claim type",
    flex: 0.9,
    minWidth: 130,
  },
];

export const getColumns = (
  claims: MdaClaim[],
  onClaimsChange: (claims: MdaClaim[]) => void,
  onViewRuleBreaks?: (claim: MdaClaim) => void,
  { visibleRowIds, renderValueFilter }: MdaColumnOptions = {},
): GridColDef<MdaClaim>[] => [
  ...getClaimColumns(claims, onClaimsChange, visibleRowIds).map((column) => {
    const field = column.field as MdaValueFilterField;
    if (!renderValueFilter || !VALUE_FILTER_FIELDS.includes(field)) {
      return column;
    }
    return {
      ...column,
      renderHeader: () =>
        renderValueFilter(field, column.headerName ?? column.field),
    };
  }),
  ...(onViewRuleBreaks ? [getRuleBreaksColumn(onViewRuleBreaks)] : []),
];
